import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TranslateIcon from '@mui/icons-material/Translate';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ArticleIcon from '@mui/icons-material/Article';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import InfoIcon from '@mui/icons-material/Info';

interface ChatMessage {
  role: 'user' | 'assistant';
  message: string;
}

interface DesktopAIChatInterfaceProps {
  chatMessage: string;
  setChatMessage: (message: string) => void;
  isChatProcessing: boolean;
  handleChatSubmit: () => void;
  chatHistory: ChatMessage[];
  handleAutoMarkdown: () => void;
  isAutoFormatting: boolean;
  isTranslating: boolean;
  translateMenuAnchor: HTMLElement | null;
  setTranslateMenuAnchor: (anchor: HTMLElement | null) => void;
  handleTranslate: (language: string) => void;
  translatedLanguages: Set<string>;
  handleUndo: () => void;
  handleRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  setComingSoonOpen: (open: boolean) => void;
  setComingSoonFeature: (feature: string) => void;
  setIsGuidelinesSidebarOpen: (open: boolean) => void;
  setIsPromptsSidebarOpen: (open: boolean) => void;
}

const DesktopAIChatInterface: React.FC<DesktopAIChatInterfaceProps> = ({
  chatMessage,
  setChatMessage,
  isChatProcessing,
  handleChatSubmit,
  chatHistory,
  handleAutoMarkdown,
  isAutoFormatting,
  isTranslating,
  translateMenuAnchor,
  setTranslateMenuAnchor,
  handleTranslate,
  translatedLanguages,
  handleUndo,
  handleRedo,
  canUndo,
  canRedo,
  setComingSoonOpen,
  setComingSoonFeature,
  setIsGuidelinesSidebarOpen,
  setIsPromptsSidebarOpen,
}) => {
  const theme = useTheme();
  
  // Disable all buttons when any processing is happening
  const isAnyProcessing = isChatProcessing || isAutoFormatting || isTranslating;

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'block' }, // Hide on mobile
        borderTop: `1px solid ${theme.palette.divider}`,
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(to bottom, #0a0a0a 0%, #0d0d0f 100%)'
          : 'linear-gradient(to bottom, #ffffff 0%, #f5f7fa 100%)',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 -8px 32px rgba(0,0,0,0.4)'
          : '0 -8px 32px rgba(0,0,0,0.08)',
      }}
    >
      <Box
        sx={{
          maxWidth: '1100px',
          mx: 'auto',
          px: { xs: 4, sm: 5, md: 8, lg: 10 },
          py: 4,
        }}
      >
        <Stack spacing={3.5}>
          {/* Action Buttons Row */}
          <Stack 
            direction="row" 
            spacing={2} 
            justifyContent="center" 
            flexWrap="wrap"
            sx={{
              pb: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Tooltip title="Advisory Note" arrow>
              <span>
                <IconButton
                  size="medium"
                  onClick={() => {
                    setChatMessage('Include an appropriate medical advisory as a note stating that this report is for informational purposes only and does not substitute a comprehensive in-person medical examination by a qualified healthcare professional.');
                  }}
                  disabled={isAnyProcessing}
                  sx={{
                    border: '2px solid',
                    borderRadius: 4,
                    borderColor: '#ff0000',
                    color: '#ff0000',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    '&:hover': {
                      borderColor: '#ff0000',
                      backgroundColor: 'rgba(255,0,0,0.08)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                ><InfoIcon/></IconButton>
              </span>
            </Tooltip>
            
            <Tooltip title="Generate production-ready patient report" arrow>
              <span>
                <IconButton
                  size="medium"
                  onClick={() => {
                    setChatMessage('Adjust the tone to a finalized institutional clinical report in first-person plural, preserving the original content, structure, and section order without modification, eliminating third-person or AI-related language.');
                  }}
                  disabled={isAnyProcessing}
                  sx={{
                    border: '2px solid',
                    borderRadius: 4,
                    borderColor: '#007AFF',
                    color: '#007AFF',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    '&:hover': {
                      borderColor: '#0051D5',
                      backgroundColor: 'rgba(0,122,255,0.08)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                ><ArticleIcon/></IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Auto-format entire document with AI" arrow>
              <span>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<AutoFixHighIcon />}
                  onClick={handleAutoMarkdown}
                  disabled={isAnyProcessing}
                sx={{
                  borderRadius: 4,
                  textTransform: 'none',
                  borderColor: '#007AFF',
                  borderWidth: 2,
                  color: '#007AFF',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: '#0051D5',
                    backgroundColor: 'rgba(0,122,255,0.08)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
                >
                  Auto-Format
                </Button>
              </span>
            </Tooltip>
            
            <Tooltip title="Translate document" arrow>
              <span>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<TranslateIcon />}
                  onClick={(e) => setTranslateMenuAnchor(e.currentTarget)}
                  disabled={isAnyProcessing}
                sx={{
                  borderRadius: 4,
                  textTransform: 'none',
                  borderColor: '#34C759',
                  borderWidth: 2,
                  color: '#34C759',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: '#2DA44E',
                    backgroundColor: 'rgba(52,199,89,0.08)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
                >
                  Translate
                </Button>
              </span>
            </Tooltip>
            <Menu
              anchorEl={translateMenuAnchor}
              open={Boolean(translateMenuAnchor)}
              onClose={() => setTranslateMenuAnchor(null)}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  mt: -1,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                },
              }}
            >
              {[
                { code: 'en', name: 'English' },
                { code: 'es', name: 'Spanish' },
                { code: 'fr', name: 'French' },
                { code: 'de', name: 'German' },
                { code: 'it', name: 'Italian' },
                { code: 'pt', name: 'Portuguese' },
                { code: 'el', name: 'Greek' },
                { code: 'zh', name: 'Chinese' },
                { code: 'ja', name: 'Japanese' },
                { code: 'ar', name: 'Arabic' },
              ].map((lang) => (
                <MenuItem
                  key={lang.code}
                  onClick={() => {
                    handleTranslate(lang.code);
                    setTranslateMenuAnchor(null);
                  }}
                  disabled={translatedLanguages.has(lang.code)}
                  sx={{
                    px: 3,
                    py: 1.5,
                    fontSize: '14px',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'rgba(52,199,89,0.1)',
                    },
                  }}
                >
                  {lang.name}
                  {translatedLanguages.has(lang.code) && ' ✓'}
                </MenuItem>
              ))}
            </Menu>
            
            <Tooltip title="Undo last change" arrow>
              <span>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<UndoIcon />}
                  onClick={handleUndo}
                  disabled={!canUndo || isAnyProcessing}
                  sx={{
                    borderRadius: 4,
                    textTransform: 'none',
                    borderColor: theme.palette.divider,
                    borderWidth: 2,
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: '#007AFF',
                      backgroundColor: 'rgba(0,122,255,0.08)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Undo
                </Button>
              </span>
            </Tooltip>
            
            <Tooltip title="Redo last undone change" arrow>
              <span>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<RedoIcon />}
                  onClick={handleRedo}
                  disabled={!canRedo || isAnyProcessing}
                  sx={{
                    borderRadius: 4,
                    textTransform: 'none',
                    borderColor: theme.palette.divider,
                    borderWidth: 2,
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: '#007AFF',
                      backgroundColor: 'rgba(0,122,255,0.08)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Redo
                </Button>
              </span>
            </Tooltip>
            
            {/* Coming Soon Features */}
            <Tooltip title="Check medical literature - Coming Soon" arrow>
              <span>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<MenuBookIcon />}
                  onClick={() => {
                    setComingSoonFeature('Medical Literature Search');
                    setComingSoonOpen(true);
                  }}
                  disabled={isAnyProcessing}
                sx={{
                  borderRadius: 4,
                  textTransform: 'none',
                  borderColor: theme.palette.divider,
                  borderWidth: 2,
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  opacity: 0.6,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: '#5E5CE6',
                    backgroundColor: 'rgba(94,92,230,0.08)',
                    transform: 'translateY(-2px)',
                    opacity: 1,
                  },
                  transition: 'all 0.3s ease',
                }}
                >
                  Literature
                </Button>
              </span>
            </Tooltip>
            
            <Tooltip title="Clinical guidelines" arrow>
              <span>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<LocalHospitalIcon />}
                  onClick={() => setIsGuidelinesSidebarOpen(true)}
                  disabled={isAnyProcessing}
                sx={{
                  borderRadius: 4,
                  textTransform: 'none',
                  borderColor: '#00C853',
                  borderWidth: 2,
                  color: '#00C853',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: '#00A843',
                    backgroundColor: 'rgba(0,200,83,0.08)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
                >
                  Guidelines
                </Button>
              </span>
            </Tooltip>
            
            <Tooltip title="Finalization templates" arrow>
              <span>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<LightbulbIcon />}
                  onClick={() => setIsPromptsSidebarOpen(true)}
                  disabled={isAnyProcessing}
                sx={{
                  borderRadius: 4,
                  textTransform: 'none',
                  borderColor: '#FFD60A',
                  borderWidth: 2,
                  color: '#FFD60A',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: '#FFB800',
                    backgroundColor: 'rgba(255,214,10,0.08)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
                >
                  Finalization
                </Button>
              </span>
            </Tooltip>
          </Stack>

          {/* Chat History Bubbles */}
          {(chatHistory.length > 0 || isChatProcessing) && (
            <Box
              sx={{
                height: '160px',
                maxHeight: '160px',
                overflow: 'auto',
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(0,0,0,0.3)'
                  : 'rgba(255,255,255,0.6)',
                backdropFilter: 'blur(10px)',
                borderRadius: 4,
                p: 3,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.palette.mode === 'dark'
                  ? 'inset 0 2px 8px rgba(0,0,0,0.3)'
                  : 'inset 0 2px 8px rgba(0,0,0,0.05)',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                  borderRadius: '10px',
                  '&:hover': {
                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                  },
                },
              }}
            >
              <Stack spacing={3}>
                {chatHistory.map((chat, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'flex-start',
                      animation: 'slideIn 0.4s ease-out',
                      '@keyframes slideIn': {
                        from: { 
                          opacity: 0, 
                          transform: chat.role === 'user' ? 'translateX(20px)' : 'translateX(-20px)' 
                        },
                        to: { opacity: 1, transform: 'translateX(0)' },
                      },
                    }}
                  >
                    {chat.role === 'assistant' && (
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 4px 12px rgba(0,122,255,0.3)',
                        }}
                      >
                        <img
                          src="/assets/noetiv.png"
                          alt="Noetiv"
                          style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                        />
                      </Box>
                    )}
                    <Box
                      sx={{
                        flex: 1,
                        px: 3,
                        py: 2,
                        borderRadius: 4,
                        background: chat.role === 'user'
                          ? 'linear-gradient(135deg, rgba(0,122,255,0.15) 0%, rgba(0,81,213,0.15) 100%)'
                          : theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.08)'
                            : 'rgba(0,0,0,0.04)',
                        border: `1px solid ${
                          chat.role === 'user' 
                            ? 'rgba(0,122,255,0.3)' 
                            : theme.palette.divider
                        }`,
                        boxShadow: chat.role === 'user'
                          ? '0 4px 12px rgba(0,122,255,0.1)'
                          : 'none',
                        maxWidth: chat.role === 'user' ? '85%' : '100%',
                        ml: chat.role === 'user' ? 'auto' : 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '14px',
                          lineHeight: 1.6,
                          color: theme.palette.text.primary,
                          fontWeight: chat.role === 'user' ? 500 : 400,
                          wordBreak: 'break-word',
                        }}
                      >
                        {chat.message}
                      </Typography>
                    </Box>
                    {chat.role === 'user' && (
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #34C759 0%, #2DA44E 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 4px 12px rgba(52,199,89,0.3)',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#fff',
                        }}
                      >
                        U
                      </Box>
                    )}
                  </Box>
                ))}
                {isChatProcessing && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'flex-start',
                      animation: 'slideIn 0.4s ease-out',
                      '@keyframes slideIn': {
                        from: { opacity: 0, transform: 'translateX(-20px)' },
                        to: { opacity: 1, transform: 'translateX(0)' },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: '0 4px 12px rgba(0,122,255,0.3)',
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.1)' },
                        },
                      }}
                    >
                      <img
                        src="/assets/noetiv.png"
                        alt="Noetiv"
                        style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                      />
                    </Box>
                    <Box
                      sx={{
                        px: 3,
                        py: 2,
                        borderRadius: 4,
                        background: theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.08)'
                          : 'rgba(0,0,0,0.04)',
                        border: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <CircularProgress size={16} sx={{ color: '#007AFF' }} />
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: theme.palette.text.secondary,
                          fontStyle: 'italic',
                        }}
                      >
                        Noetiv is thinking...
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {/* Enhanced Chat Input Area */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 2,
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255,255,255,0.05)'
                : '#ffffff',
              backdropFilter: 'blur(10px)',
              borderRadius: 5,
              p: 2.5,
              border: `2px solid ${theme.palette.divider}`,
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              '&:focus-within': {
                borderColor: '#007AFF',
                boxShadow: '0 8px 32px rgba(0,122,255,0.2)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
                flexShrink: 0,
                boxShadow: '0 4px 12px rgba(0,122,255,0.3)',
              }}
            >
              <ChatIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            
            <TextField
              fullWidth
              multiline
              maxRows={5}
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleChatSubmit();
                }
              }}
              placeholder="Ask Noetiv to refine, add sections, improve tone, fix grammar, or make any changes..."
              disabled={isAnyProcessing}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: '15px',
                  lineHeight: 1.6,
                  color: theme.palette.text.primary,
                  fontWeight: 400,
                },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '15px',
                },
                '& ::placeholder': {
                  opacity: 0.6,
                },
              }}
            />
            
            <Tooltip title="Send message" arrow>
              <span>
                <IconButton
                  onClick={handleChatSubmit}
                  disabled={!chatMessage.trim() || isAnyProcessing}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: (!chatMessage.trim() || isAnyProcessing)
                      ? theme.palette.action.disabledBackground
                      : 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
                    color: '#fff',
                    boxShadow: (!chatMessage.trim() || isAnyProcessing)
                      ? 'none'
                      : '0 4px 16px rgba(0,122,255,0.4)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: (!chatMessage.trim() || isAnyProcessing) ? 'none' : 'scale(1.1)',
                      boxShadow: (!chatMessage.trim() || isAnyProcessing) 
                        ? 'none' 
                        : '0 6px 24px rgba(0,122,255,0.5)',
                      background: (!chatMessage.trim() || isAnyProcessing)
                        ? theme.palette.action.disabledBackground
                        : 'linear-gradient(135deg, #0051D5 0%, #003da5 100%)',
                    },
                    '&:active': {
                      transform: (!chatMessage.trim() || isAnyProcessing) ? 'none' : 'scale(0.95)',
                    },
                  }}
                >
                  <SendIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default DesktopAIChatInterface;
